import { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { StudyMaterial } from '../../../lib/supabase';
import { CategoryManager } from '../../../components/feature/CategoryManager';

interface MaterialFormProps {
  userId: string;
  material?: StudyMaterial | null;
  onSaved: () => void;
  onClose: () => void;
}

export default function MaterialForm({
  userId,
  material,
  onSaved,
  onClose,
}: MaterialFormProps) {
  const [title, setTitle] = useState(material?.title || '');
  const [subject, setSubject] = useState(material?.subject || '');
  const [description, setDescription] = useState(material?.description || '');
  const [fileUrl, setFileUrl] = useState(material?.file_url || '');
  const [isPublic, setIsPublic] = useState<boolean>(
    (material as StudyMaterial & { is_public?: boolean })?.is_public ?? true
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isEdit = !!material;

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('タイトルを入力してください');
      return;
    }
    if (!subject) {
      setError('科目を選択してください');
      return;
    }

    setSaving(true);
    setError('');
    try {
      if (isEdit && material) {
        const { error: dbErr } = await supabase
          .from('study_materials')
          .update({
            title: title.trim(),
            subject,
            description: description.trim(),
            file_url: fileUrl.trim() || null,
            is_public: isPublic,
            updated_at: new Date().toISOString(),
          })
          .eq('id', material.id)
          .eq('user_id', userId);
        if (dbErr) throw dbErr;
      } else {
        const { error: dbErr } = await supabase
          .from('study_materials')
          .insert({
            title: title.trim(),
            subject,
            description: description.trim(),
            file_url: fileUrl.trim() || null,
            is_public: isPublic,
            user_id: userId,
          });
        if (dbErr) throw dbErr;
      }
      onSaved();
    } catch (e) {
      console.error(e);
      setError('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="text-lg font-bold text-gray-800 dark:text-white">
            {isEdit ? '資料を編集' : '資料を追加'}
          </h3>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600 dark:text-gray-300"></i>
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          {/* タイトル */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              タイトル <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 100))}
              placeholder="資料のタイトル"
              maxLength={100}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 bg-white dark:bg-gray-900 dark:text-white"
            />
          </div>

          {/* 科目 - CategoryManager */}
          <CategoryManager
            categoryType="subject"
            selectedCategory={subject}
            onSelectCategory={setSubject}
            label="科目"
            placeholder="科目を選択"
          />

          {/* 説明 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              説明
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 500))}
              placeholder="資料の説明（任意）"
              rows={3}
              maxLength={500}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 bg-white dark:bg-gray-900 dark:text-white resize-none"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">
              {description.length}/500
            </p>
          </div>

          {/* ファイルURL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              ファイルURL（Google Drive等）
            </label>
            <input
              type="url"
              value={fileUrl}
              onChange={(e) => setFileUrl(e.target.value)}
              placeholder="https://drive.google.com/..."
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 bg-white dark:bg-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-400 mt-1">
              Google DriveやDropboxなどの共有リンクを入力してください
            </p>
          </div>

          {/* 公開設定 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              公開設定
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setIsPublic(true)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  isPublic
                    ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-400 text-teal-700 dark:text-teal-400 ring-2 ring-teal-300 dark:ring-teal-600'
                    : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <i className="ri-global-line text-base"></i>
                全体公開
              </button>
              <button
                type="button"
                onClick={() => setIsPublic(false)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  !isPublic
                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-400 text-amber-700 dark:text-amber-400 ring-2 ring-amber-300 dark:ring-amber-600'
                    : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <i className="ri-lock-line text-base"></i>
                自分だけ
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1.5">
              {isPublic ? '全ユーザーに公開されます' : '自分だけが閲覧できます'}
            </p>
          </div>

          {/* アクションボタン */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              キャンセル
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {saving ? '保存中...' : isEdit ? '更新する' : '追加する'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}